import React, { useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import ShopSection from "@/components/ShopSection";
import BrandStory from "@/components/BrandStory";
import Testimonials from "@/components/Testimonials";
import FAQ from "@/components/FAQ";
import Footer from "@/components/Footer";
import CartSheet from "@/components/CartSheet";
import CheckoutSuccess from "@/components/CheckoutSuccess";
import PromoBanner from "@/components/PromoBanner";
import RaffleBanner from "@/components/RaffleBanner";
import Feedback from "@/components/Feedback";
import LoginPage from "@/components/LoginPage";
import RegisterPage from "@/components/RegisterPage";
import RaffleDashboard from "@/components/RaffleDashboard";
import AuthCallback from "@/components/AuthCallback";
import AdminPanel from "@/components/AdminPanel";
import { AuthProvider } from "@/components/AuthContext";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = ({ cart, addToCart, removeFromCart, updateQuantity, cartOpen, setCartOpen }) => {
  return (
    <>
      <HeroSection />
      <PromoBanner onOpenCart={() => setCartOpen(true)} />
      <ShopSection addToCart={addToCart} />
      <RaffleBanner />
      <BrandStory />
      <Testimonials />
      <Feedback />
      <FAQ />
    </>
  );
};

function App() {
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);

  const addToCart = useCallback((product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    setCartOpen(true);
  }, []);

  const removeFromCart = useCallback((productId) => {
    setCart(prev => prev.filter(item => item.id !== productId));
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity <= 0) {
      setCart(prev => prev.filter(item => item.id !== productId));
      return;
    }
    setCart(prev =>
      prev.map(item =>
        item.id === productId ? { ...item, quantity } : item
      )
    );
  }, []);

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0C0C0C' }}>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter cart={cart} cartOpen={cartOpen} setCartOpen={setCartOpen} cartCount={cartCount} addToCart={addToCart} removeFromCart={removeFromCart} updateQuantity={updateQuantity} />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

function AppRouter({ cart, cartOpen, setCartOpen, cartCount, addToCart, removeFromCart, updateQuantity }) {
  const location = useLocation();
  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <Navbar cartCount={cartCount} onCartClick={() => setCartOpen(true)} />
      <CartSheet
        cart={cart}
        open={cartOpen}
        onOpenChange={setCartOpen}
        removeFromCart={removeFromCart}
        updateQuantity={updateQuantity}
      />
      <Routes>
        <Route
          path="/"
          element={
            <LandingPage
              cart={cart}
              addToCart={addToCart}
              removeFromCart={removeFromCart}
              updateQuantity={updateQuantity}
              cartOpen={cartOpen}
              setCartOpen={setCartOpen}
            />
          }
        />
        <Route path="/success" element={<CheckoutSuccess />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/raffle" element={<RaffleDashboard />} />
        <Route path="/admin" element={<AdminPanel />} />
      </Routes>
      <Footer />
      <Toaster position="bottom-right" />
    </>
  );
}

export default App;
